/**
 * Phase 4b PR 3 — Auth context provider for the React app.
 *
 * Wraps the app in main.tsx. Children read auth state via the
 * `useUser` and `useAuthFetch` hooks (this provider's only public API
 * is the React context).
 *
 * State machine:
 *   - On mount: loading=true, supabase=null, session=null, error=null
 *   - Fetch /api/config + create Supabase client → if it throws, set
 *     error and STOP (loading=false). Children that don't need auth
 *     still render fine.
 *   - Once client exists: call supabase.auth.getSession() → set
 *     session/user (may be null if not signed in)
 *   - Subscribe to onAuthStateChange to keep state synced through
 *     sign-in / sign-out / token refresh
 *   - Unsubscribe on unmount
 *
 * Critically: this provider does NOT gate render. It exposes auth
 * state; consumers decide what to do (redirect to landing, show
 * spinner, etc.). PR 3 ships only the primitives; PR 4 chat shell
 * consumes them.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, SupabaseClient, User } from '@supabase/supabase-js';
import { isNativeApp } from '../lib/platform';
import {
  NativeBridgeTimeoutError,
  NativeOAuthPluginUnavailableError,
  openNativeOAuth,
  registerNativeAuthListener,
} from '../lib/nativeAuth';
import {
  APPLE_SHEET_TIMEOUT_MS,
  ApplePluginUnavailableError,
  AppleSheetTimeoutError,
  signInWithAppleNative,
} from '../lib/appleAuth';
// apple-signin-spinner — the two typed-state primaries live with the aux
// clauses in oauthChipCopy (that module must stay runtime-independent of
// this one); mapOAuthError below is what routes onto them.
import {
  OAUTH_APPLE_TIMEOUT_PRIMARY,
  OAUTH_APPLE_UNAVAILABLE_PRIMARY,
  OAUTH_NATIVE_UNAVAILABLE_PRIMARY,
  OAUTH_NOT_READY_PRIMARY,
} from '../lib/oauthChipCopy';
import { getSupabaseClient, retryConfigRead, subscribeServerFlags } from '../lib/supabase';
import { clearPersistedState, purgeForeignPersistedState } from '../hooks/onboardingReducer';
import { clearTodayLayout } from '../lib/todayLayoutMemory';
import { clearPlanShape } from '../lib/settingsPlanShape';
import { clearAllQueues, clearQueuesExcept } from '../lib/roomSendQueue';
import { purgeForeignForkChoice } from '../components/onboarding/forkStorage';

/**
 * F-201 / F-113 — on every auth-state change, drop onboarding localStorage
 * left behind by a DIFFERENT user in this browser (the step-0 fork choice and
 * the wizard blob, which can hold a minor's PAR-Q + body metrics). Keyed by
 * the signed-in user id: the current user's OWN state is preserved. Best-
 * effort + idempotent, so it's safe to call on token refresh and getSession.
 *
 * B110 (2026-09-21, HELD OUT) — filed to stop `purgeForeignPersistedState`
 * from claiming an UNCLAIMED wizard blob (one with no user stamp) for the
 * signing-in user and retire the B26 rescue that does so; the founder has
 * not yet ruled, so B26 is unchanged here and an unstamped blob is still
 * claimable by whichever account signs in first. The fork choice has no
 * such case — the step-0 fork only ever renders for a signed-in user — so
 * it keeps its purge-on-legacy rule, unaffected either way.
 */
function purgeForeignOnboardingState(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    purgeForeignPersistedState(userId);
    purgeForeignForkChoice(userId);
  } catch {
    /* best-effort hygiene — never block auth on it */
  }
}

/** apple-login-v1 — the two live login providers. One alias so the provider
 *  never travels as a bare string that a typo could widen. */
export type OAuthProvider = 'google' | 'apple';

export interface AuthContextValue {
  /** Supabase client — null until /api/config fetch completes. */
  supabase: SupabaseClient | null;
  /** Active session, or null if not signed in. */
  session: Session | null;
  /** Convenience accessor — same as session?.user ?? null. */
  user: User | null;
  /** True until the initial /api/config + getSession() handshake completes. */
  loading: boolean;
  /**
   * Non-null when the CONFIG could not be read — there is no client, so there
   * is no session to find, of anybody. Children may render a fallback.
   *
   * ⚖️ B167 fix-3 (round-51 critic F1) — NARROWED TO CONFIG FAILURES. It used
   * to catch everything the init did, so a throw AFTER the client existed (the
   * native deep-link listener, `getSession` on stale storage) latched an error
   * beside a live client, and `plan-first-look.tsx` — which holds on this —
   * rendered a chrome-free blank on a device where every other route worked. A
   * failure past the client is logged and surfaces through the existing
   * session-null path instead.
   */
  error: Error | null;
  /**
   * ⚖️ B167 fix-3 (round-51 critic F1) — TRUE ONCE THE PROVIDER HAS STOPPED
   * ASKING. `error` alone cannot say that: it is set the moment the first
   * bounded cycle gives up, while the re-ask is still to come. A route that
   * holds on `error` ends its hold HERE — nothing further changes on this page
   * without a reload — and never before, or it acts while an answer is still
   * on its way. When it is true, `error` is set, `loading` is false and
   * `supabase` is null.
   */
  configExhausted: boolean;
  /** Convenience signOut — wraps supabase.auth.signOut. No-op if client not ready. */
  signOut: () => Promise<void>;
  /** Phase 6b0 — initiate Google OAuth via Supabase. No-op if client not ready.
   *  Absolute redirect URL `${window.location.origin}/v2/today`. On failure,
   *  populates `oauthError` (the caller renders the chip). B178 fix-3 — a
   *  call is dropped SILENTLY (no chip, no log) while a sign-in is already
   *  pending for this provider or the other; see `pendingSignInTokenRef`. */
  signInWithGoogle: () => Promise<void>;
  /** apple-login-v1 — initiate Sign in with Apple. No-op if client not ready.
   *  Inside the shell this runs the NATIVE sheet (lib/appleAuth.ts) and ONLY
   *  the native sheet: a missing plugin surfaces as a chip
   *  (apple-signin-spinner — no silent browser reroute), and a wedged bridge
   *  call times out into a retryable chip instead of an infinite spinner.
   *  On web it is `signInWithOAuth({ provider: 'apple' })`, the same path
   *  Google rides. A cancelled sheet resolves SILENTLY — no error chip.
   *  B178 fix-3 — same shared-guard drop as `signInWithGoogle` above. */
  signInWithApple: () => Promise<void>;
  /** Phase 6b0 — OAuth error chip message ("Sign-in cancelled.", "Couldn't reach
   *  Google."/"Couldn't reach Apple.", "Sign-in failed.", or B178 fix-2's
   *  "Sign-in is not ready yet." for a tap that never got a client). Null when
   *  no error pending. */
  oauthError: string | null;
  /** apple-login-v1 — WHICH provider produced `oauthError`. Two of the three
   *  chip primaries ("Sign-in cancelled." / "Sign-in failed.") are
   *  provider-neutral by design, so a surface cannot recover the provider from
   *  the string; without this it would keep offering to "pick another Google
   *  account" after an Apple failure. Null whenever `oauthError` is null. */
  oauthErrorProvider: OAuthProvider | null;
  /** Phase 6b0 — clear the OAuth error chip. Caller invokes on next CTA click. */
  clearOAuthError: () => void;
}

const DEFAULT_VALUE: AuthContextValue = {
  supabase: null,
  session: null,
  user: null,
  loading: true,
  error: null,
  configExhausted: false,
  signOut: async () => {
    /* no-op until client is ready */
  },
  signInWithGoogle: async () => {
    /* no-op until client is ready */
  },
  signInWithApple: async () => {
    /* no-op until client is ready */
  },
  oauthError: null,
  oauthErrorProvider: null,
  clearOAuthError: () => {
    /* no-op */
  },
};

/**
 * Phase 6b0 — map Supabase OAuth error shapes to chip copy.
 *
 * Three primary buckets per design `direction.md §4`:
 *   - Cancelled / denied by user → "Sign-in cancelled."
 *   - Network / fetch failure   → "Couldn't reach Google."
 *   - Anything else             → "Sign-in failed."
 *
 * Each chip has an auxiliary clause that the rendering surface
 * (LandingHero) tacks on after the primary copy. The helper returns
 * only the primary; the aux clauses live in the component.
 *
 * apple-login-v1 — the network bucket NAMES the provider, so `provider`
 * was added with a 'google' DEFAULT: every existing call site and every
 * existing string is unchanged, and only an Apple attempt can produce
 * "Couldn't reach Apple.".
 */
export function mapOAuthError(
  err: unknown,
  provider: OAuthProvider = 'google',
): string {
  if (err === null || err === undefined) return 'Sign-in failed.';
  // apple-signin-spinner — typed states first: these carry MORE information
  // than their messages, and the keyword heuristics below would misfile them
  // ("timed out" would read as a network failure when the network was fine
  // and the native bridge call simply never settled).
  if (err instanceof AppleSheetTimeoutError) return OAUTH_APPLE_TIMEOUT_PRIMARY;
  if (err instanceof ApplePluginUnavailableError) return OAUTH_APPLE_UNAVAILABLE_PRIMARY;
  // B207/R14 — the native ASWebAuthenticationSession plugin is not in this
  // build. Typed, ahead of the keyword buckets, for the same reason as the
  // two Apple types above: it carries more information than its message.
  if (err instanceof NativeOAuthPluginUnavailableError) return OAUTH_NATIVE_UNAVAILABLE_PRIMARY;
  // b178-fix4 — a wedged native-bridge open (Browser.open never settling) is
  // not a network failure: nothing was ever reached. The message says "timed
  // out", which the keyword heuristics below would otherwise misfile as
  // "Couldn't reach Google." — decision X's ratified not-ready pair is the
  // true state (the same pair a tap-with-no-client shows), so it is matched
  // by type, ahead of the keyword buckets, same as the two Apple types above.
  if (err instanceof NativeBridgeTimeoutError) return OAUTH_NOT_READY_PRIMARY;
  const message =
    typeof err === 'string'
      ? err
      : err instanceof Error
        ? err.message
        : String(err);
  const lower = message.toLowerCase();
  // User-cancelled or denied consent — Supabase typically returns an
  // `access_denied` error code or wording matching "cancel"/"denied".
  if (
    lower.includes('cancel') ||
    lower.includes('denied') ||
    lower.includes('access_denied') ||
    lower.includes('user_cancelled')
  ) {
    return 'Sign-in cancelled.';
  }
  // Network / fetch failure — typically wraps `TypeError: Failed to
  // fetch` or includes "network" / "offline" / "fetch".
  if (
    lower.includes('network') ||
    lower.includes('fetch') ||
    lower.includes('offline') ||
    lower.includes('econnreset') ||
    lower.includes('timeout')
  ) {
    return provider === 'apple' ? "Couldn't reach Apple." : "Couldn't reach Google.";
  }
  return 'Sign-in failed.';
}

/**
 * B149 — how many times the init re-asks for the config after a failure.
 * `lib/supabase` already retries the read four times behind a single
 * `getSupabaseClient()` call, so this is the second chance a page gets, not
 * the first, and the pair is bounded — never a spinner with no end.
 *
 * B149 fix-3 — AND THE THREE-SECOND TIMER IS GONE. It was a guess about when
 * asking again might be worth it, made on top of a cycle that had already
 * spent four reads over seconds of wall clock; the re-ask now runs the moment
 * that cycle gives up, through `retryConfigRead` — the one door through the
 * module's exhausted mark (F4). One page load buys at most two cycles.
 */
const AUTH_INIT_RETRIES = 1;

/**
 * B147 — THE BOOT'S getSession() RACES ITS OWN DEADLINE.
 *
 * `client.auth.getSession()` refreshes an expired access token against
 * Supabase (auth-js's `_initialize()` → `_recoverAndRefresh()`, kicked off
 * automatically the moment the client is constructed in `lib/supabase.ts`) —
 * a network round trip that sits directly in the boot's critical path:
 * `loading` used to clear only in this effect's `finally`, after this call
 * settled. On a half-open network (a captive portal: sockets accepted,
 * nothing answered) it never settles, and every guarded route held on
 * `loading` rides the platform's own request timeout (about 60 s on
 * WKWebView) instead of anything this app bounds — the worst of the three
 * unbounded terms behind B134's blank (B47 fix8 covers the other two: the
 * role fetch and the season read). In the spirit of those 10 s boundary
 * deadlines and `SIGN_IN_TAP_WAIT_CAP_MS` below: 8 s, so the boot's own hold
 * is shorter than the tap's own wait built on top of it.
 *
 * On the deadline: `loading` clears and the boot falls through to the
 * signed-out surface (`session` stays null) — the vendored auth-js client
 * (2.105.1) exposes no synchronous accessor for a stored session, only the
 * async `getSession()` this deadline is racing, so there is no stored value
 * to fall back to here. `getSession()` itself is NOT abandoned or aborted:
 * it and the `onAuthStateChange` subscription registered right after it
 * share the same underlying `initializePromise`/lock (auth-js), so a network
 * that recovers after the deadline still delivers the real session through
 * that subscription's `INITIAL_SESSION` replay — the boot's own re-ask, on
 * the next auth event, with no reload needed.
 */
const AUTH_GET_SESSION_DEADLINE_MS = 8_000;

/** Sentinel the deadline race resolves to — never a value `getSession()`
 *  could itself produce. Mirrors `useAthleteContext.tsx`'s `READ_DEADLINE`. */
const AUTH_GET_SESSION_DEADLINE = Symbol('auth-get-session-deadline');

/**
 * B178 fix-2 — THE TAP'S OWN CAP, SEPARATE FROM THE CONFIG CYCLE.
 *
 * `bootedClient` (below) waits for the boot as a whole — the re-ask
 * included — so a tap over the first cycle's failure no longer raises a
 * chip on an app that goes on to boot. But "wait for the boot" without a
 * bound of its own is a button that reads "Signing in…", disabled, for as
 * long as both cycles take to exhaust (about 38 s each; see
 * `CONFIG_READ_TIMEOUT_MS` in `lib/supabase`). Ten seconds is the founder's
 * number: past it the tap gives up on THIS wait — the boot keeps running
 * behind it — and the buttons release with a true, retryable sentence
 * instead of staying a dead control until a cycle neither of them started
 * finishes.
 */
const SIGN_IN_TAP_WAIT_CAP_MS = 10_000;

/**
 * b178-fix4 — THE BELT: `pendingSignInTokenRef` clears itself on a bound of
 * its own, independent of the `finally` that already clears it and independent
 * of any single call's own timeout (the native Google race above, Apple's
 * `APPLE_SHEET_TIMEOUT_MS`). Those are each scoped to one await; this is
 * scoped to the guard itself, so a future await added anywhere inside either
 * handler's try block — one this lane did not anticipate — still cannot pin
 * the guard past a finite bound.
 *
 * ⚖️ post-deploy critic (HIGH, closed here) — 45 s (the first cut) does NOT
 * bound the Apple chain. `signInWithAppleNative` (lib/appleAuth.ts) runs
 * THREE sequential legs, each separately raced against `appleTimeoutMs`
 * (shipped value `APPLE_SHEET_TIMEOUT_MS`): plugin resolution, `authorize`
 * (up to the full bound — the athlete may be reading first-run consent or
 * typing an Apple ID password), then `exchangeIdentityToken` (up to the full
 * bound again — a plain network call). Plugin resolution is a local bundle
 * chunk that REJECTS rather than hangs (appleAuth.ts's own note) — it does
 * not meaningfully lengthen a LEGITIMATE flow, so it is not counted as a full
 * leg below; a genuine wedge there is still caught by its own bound and
 * surfaces as Apple's own timeout chip, independent of this guard. So the
 * longest LEGITIMATE chain this belt must outlast is: the tap's own boot wait
 * (`SIGN_IN_TAP_WAIT_CAP_MS`) + `authorize` + `exchangeIdentityToken` — DERIVED
 * from the Apple constant, not a literal, so the two cannot drift apart:
 *
 *   SIGN_IN_TAP_WAIT_CAP_MS (10 s) + APPLE_SHEET_TIMEOUT_MS (30 s)
 *     + APPLE_SHEET_TIMEOUT_MS (30 s) + headroom (10 s) = 80 s
 *
 * A 45 s belt would release mid-flight on a legitimate ~55–58 s Apple
 * sign-in (boot wait + a slow `authorize` + a slow `exchangeIdentityToken`),
 * and a tap on the OTHER surface during that window would start a SECOND
 * flow and overwrite the shared `pendingProviderRef` — the misattributed-chip
 * double-flow class fix-3 exists to prevent. 80 s comfortably exceeds it.
 */
const PENDING_SIGN_IN_GUARD_HEADROOM_MS = 10_000;
const PENDING_SIGN_IN_GUARD_MS =
  SIGN_IN_TAP_WAIT_CAP_MS + APPLE_SHEET_TIMEOUT_MS * 2 + PENDING_SIGN_IN_GUARD_HEADROOM_MS;

/**
 * The value `armPendingSignIn` actually uses. Production never changes it.
 * Exists so a real-timer test can prove the belt fires on its own — without
 * this, proving it would mean a test that really waits 80s. Mirrors
 * `appleAuth.ts`'s `appleTimeoutMs` / `lib/nativeAuth.ts`'s
 * `nativeOAuthTimeoutMs`.
 */
let pendingSignInGuardMs: number = PENDING_SIGN_IN_GUARD_MS;

/** Test-only: shorten the belt so a real-timer test finishes in ms. Call
 *  with no argument to restore the shipped value. */
export function __setPendingSignInGuardMsForTest(ms?: number): void {
  pendingSignInGuardMs = ms ?? PENDING_SIGN_IN_GUARD_MS;
}

const AuthContext = createContext<AuthContextValue>(DEFAULT_VALUE);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  /** B167 fix-3 (F1) — the provider has stopped asking; see the context field. */
  const [configExhausted, setConfigExhausted] = useState<boolean>(false);
  // Phase 6b0 — OAuth error chip state lives on the context so both
  // landing CTAs (nav pill + hero pill) read/clear without prop-
  // drilling. Per design `direction.md §6` the error chip is anchored
  // under the hero CTA; the nav pill never renders one.
  const [oauthError, setOauthError] = useState<string | null>(null);
  // apple-login-v1 — the provider that produced `oauthError`, so the chip's
  // recovery clause can name the right account picker.
  const [oauthErrorProvider, setOauthErrorProvider] = useState<OAuthProvider | null>(
    null,
  );

  // Keep latest supabase in a ref so the signOut closure is stable
  // across renders without forcing consumers to re-render.
  const supabaseRef = useRef<SupabaseClient | null>(null);

  /**
   * B178 fix-2 — has the boot stopped asking, read synchronously. State
   * (`configExhausted`) is what children render on; a tap that arrives after
   * the mark needs the answer in the same tick, before this render's state
   * has necessarily committed, which is exactly why `supabaseRef` above
   * exists for the client half of the same question.
   */
  const configExhaustedRef = useRef(false);

  /**
   * B178 fix-2 — THE BOOT AS A WHOLE, ONE PROMISE FOR ITS ENTIRE LIFE.
   *
   * `getSupabaseClient()` and `retryConfigRead()` are each ONE cycle;
   * `bootedClient` (below) needs to hear about BOTH, because rejecting cycle
   * 1's promise is not the boot failing — cycle 2 (the re-ask) is still to
   * come. This promise is created once, lazily, and resolved from exactly two
   * places in the effect below: the moment a client is confirmed (either
   * cycle), or the moment `configExhausted` is set (both cycles spent). A tap
   * that arrives after either has already happened gets the settled value at
   * once; nothing here starts a new read.
   */
  const bootOutcomeRef = useRef<{
    promise: Promise<{ client: SupabaseClient } | { client: null }>;
    resolve: (result: { client: SupabaseClient } | { client: null }) => void;
  } | null>(null);

  function getBootOutcome() {
    if (!bootOutcomeRef.current) {
      let resolve!: (result: { client: SupabaseClient } | { client: null }) => void;
      const promise = new Promise<{ client: SupabaseClient } | { client: null }>((res) => {
        resolve = res;
      });
      bootOutcomeRef.current = { promise, resolve };
    }
    return bootOutcomeRef.current;
  }

  /**
   * apple-login-v1 — WHICH provider the in-flight system-browser hop belongs
   * to. A ref, not state, and deliberately so: the ONLY reader is the
   * `appUrlOpen` callback registered once at mount, which closes over its
   * environment for the life of the process. A state value would be captured
   * at registration time and would report 'google' forever; the ref is read at
   * callback time and reports what the athlete actually tapped.
   *
   * This is the one place an error can arrive with NO synchronous call stack
   * to attribute it to: the deep link fires long after `signInWithApple`
   * returned, so `mapOAuthError` would otherwise take its 'google' default and
   * an Apple failure would read "Couldn't reach Google." Set by both sign-in
   * methods BEFORE they hand off to the browser.
   *
   * Seeded 'google' because that is the only provider that can reach the
   * listener until `apple_login_v1` flips.
   */
  const pendingProviderRef = useRef<OAuthProvider>('google');

  /**
   * B178 fix-3 — ONE SIGN-IN IN FLIGHT, HOWEVER MANY TAPS OR SURFACES. Before
   * this lane `LandingHero` and `LandingNav` each held their own pending
   * state for their OWN button's label only — a tap on the hero and a tap on
   * the nav pill during the same boot wait each ran their own
   * `signInWithGoogle()`, parking two `signInWithOAuth` starts on one boot (a
   * double PKCE start; the second fails), and `run-spike.tsx`'s control had
   * no guard at all. The guard belongs here, once, shared by both providers
   * (a Google tap and an Apple tap are still ONE sign-in in flight): set
   * synchronously before the first `await` in either handler below — so a
   * second call arriving before this tick's `await` still sees it — and
   * cleared in `finally` once the call is fully settled (the boot wait's
   * cap, a fully exhausted boot, or the OAuth attempt itself resolving).
   * Per the founder's ruling, the drop is silent: no chip, no log, the first
   * call's outcome is what the user sees. Not lifted to state on purpose —
   * nothing here needs to re-render on it; the surfaces' own state still
   * drives their button label.
   *
   * b178-fix4 — set and cleared ONLY through `armPendingSignIn` /
   * `releasePendingSignIn` below, which pair this flag with its own belt
   * timer (`PENDING_SIGN_IN_GUARD_MS`) so the `finally` above is never the
   * only thing standing between a wedged await and a permanently stuck flag.
   *
   * ⚖️ post-deploy critic (HIGH, closed here) — OWNERSHIP. A bare boolean has
   * no notion of WHICH attempt currently holds the guard, so an abandoned
   * attempt's own belt (or its own very-late `finally`, once its unwaited
   * network call eventually settles — a real promise can never be cancelled)
   * could clear a guard a NEWER, still-live attempt is genuinely using: the
   * abandoned attempt's release races the newer attempt's own release. Each
   * `armPendingSignIn()` call now mints a fresh token (`pendingSignInTokenRef`
   * replaces the boolean); `releasePendingSignIn(token)` — called from either
   * a `finally` or the belt timer, the SAME function either way — is a no-op
   * unless `token` still matches the CURRENT owner. A stale release from an
   * abandoned attempt can therefore never touch a newer attempt's guard.
   */
  const pendingSignInTokenRef = useRef<symbol | null>(null);

  /** b178-fix4 — the belt timer's handle; see `PENDING_SIGN_IN_GUARD_MS` and
   *  `armPendingSignIn`/`releasePendingSignIn` below. Cleared alongside the
   *  guard so a normal settle never leaves a stray timer armed. */
  const pendingSignInTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** b178-fix4 — the release half, shared by BOTH the `finally` and the belt
   *  timer below, so ownership is checked in exactly one place. A no-op
   *  unless `token` is still the CURRENT owner: a stale call — the belt or
   *  the `finally` of an attempt a newer one has since superseded — never
   *  clears a guard it no longer owns. */
  const releasePendingSignIn = useRef((token: symbol): void => {
    if (pendingSignInTokenRef.current !== token) return;
    if (pendingSignInTimerRef.current !== null) {
      clearTimeout(pendingSignInTimerRef.current);
      pendingSignInTimerRef.current = null;
    }
    pendingSignInTokenRef.current = null;
  }).current;

  /** b178-fix4 — claim the guard with a fresh ownership token AND arm its own
   *  release, so no single await inside the try block that follows can pin it
   *  past `PENDING_SIGN_IN_GUARD_MS`. Returns the token — the caller MUST hold
   *  it and pass it to `releasePendingSignIn` in its own `finally`. */
  const armPendingSignIn = useRef((): symbol => {
    const token = Symbol('pendingSignIn');
    pendingSignInTokenRef.current = token;
    pendingSignInTimerRef.current = setTimeout(() => {
      releasePendingSignIn(token);
    }, pendingSignInGuardMs);
    return token;
  }).current;

  /** B417 follow-up (founder 2026-10-21) — true only while the chip on screen is
   *  the one `raiseNotReadyError` raised for a tap that had no client. Adoption
   *  (`watchForLateClient`) clears exactly that chip: it is stale the moment a
   *  client exists. Judged by who raised it, never by its text: a
   *  `NativeBridgeTimeoutError` chip (`mapOAuthError`) reads the same words for a
   *  different reason and is left alone. Every `setOauthError` below keeps it true. */
  const notReadyChipRef = useRef(false);

  /** Raise the chip, remembering who caused it. Every `setOauthError` in this
   *  provider goes through here so the two can never drift apart. */
  const raiseOAuthError = useRef((err: unknown, provider: OAuthProvider) => {
    notReadyChipRef.current = false;
    setOauthError(mapOAuthError(err, provider));
    setOauthErrorProvider(provider);
  }).current;

  const clearOAuthErrorState = useRef(() => {
    notReadyChipRef.current = false;
    setOauthError(null);
    setOauthErrorProvider(null);
  }).current;

  /** B178 fix-2 — decision X's ratified pair, for a tap that never got a
   *  client: the cap was reached, or the boot exhausted both cycles first.
   *  Neither goes through `mapOAuthError` — nothing was attempted, so none of
   *  its buckets are true — the chip is this fixed pair either way. */
  const raiseNotReadyError = useRef((provider: OAuthProvider) => {
    notReadyChipRef.current = true;
    setOauthError(OAUTH_NOT_READY_PRIMARY);
    setOauthErrorProvider(provider);
  }).current;

  /**
   * ⚖️ B178 fix-3 (round-51 critic F5), fix-2 — A TAP DURING THE BOOT IS NOT A
   * FAILURE, AND THE TAP'S OWN WAIT IS NOT UNBOUNDED.
   *
   * Both signed-out surfaces paint their CTA before the client exists (the
   * landing hero renders regardless of `loading`; the native welcome the
   * same), so on a cold start against a cold Lambda a tap at 0.6 s used to hit
   * the sign-in handlers' null-client branch and raise "Sign-in failed." with
   * nothing failed and nothing attempted — on the happy path, needing no
   * outage at all.
   *
   * fix-3 waited on cycle 1's own promise, which meant a tap that spanned
   * cycle 1's failure still raised the chip the instant that cycle gave up —
   * even when the re-ask (cycle 2) was about to land. fix-2: the tap waits on
   * `getBootOutcome()` instead, which settles only when the boot is truly
   * over — a client confirmed on EITHER cycle, or `configExhausted` reached
   * after both. And that wait is now capped at `SIGN_IN_TAP_WAIT_CAP_MS`: past
   * the cap this tap gives up (the boot keeps running behind it; a later tap
   * gets whatever it eventually decided) rather than leaving the buttons a
   * disabled "Signing in…" for as long as two whole cycles take to exhaust.
   */
  const bootedClient = useRef(
    async (): Promise<{ client: SupabaseClient } | { client: null }> => {
      const ready = supabaseRef.current;
      if (ready) return { client: ready };
      if (configExhaustedRef.current) return { client: null };
      const outcome = getBootOutcome().promise;
      return Promise.race([
        outcome,
        new Promise<{ client: null }>((resolve) => {
          setTimeout(() => resolve({ client: null }), SIGN_IN_TAP_WAIT_CAP_MS);
        }),
      ]);
    },
  ).current;

  useEffect(() => {
    let unmounted = false;
    let unsubscribe: (() => void) | null = null;
    let removeDeepLinkListener: (() => void) | null = null;
    let retriesLeft = AUTH_INIT_RETRIES;
    let running = false;
    // B417 — the late-client watch (`watchForLateClient` below); null until the
    // boot gives up with no client, and null again once it has adopted one.
    let stopWatchingForClient: (() => void) | null = null;
    let adoptionAttempting = false;

    // B417 — `adopted` is the client `watchForLateClient` found; it stands in
    // for the config read, and everything after the read is the same code.
    const init = async (isReask: boolean, adopted?: SupabaseClient): Promise<void> => {
      if (unmounted || running || supabaseRef.current) return;
      running = true;
      // B147 — cleared in the outer `finally` below, whichever way the race
      // (or an early return) got here; a read that settles before the
      // deadline leaves no timer behind, byte-identical to before this fix.
      let deadlineTimer: ReturnType<typeof setTimeout> | undefined;

      // ── The config. The ONLY failure that sets `error` ──────────────────
      let client: SupabaseClient;
      try {
        // B149 fix-3 (F4) — the re-ask is the one caller allowed through the
        // module's exhausted mark; the first ask joins whatever cycle is
        // already in flight.
        client = adopted ?? (isReask ? await retryConfigRead() : await getSupabaseClient());
      } catch (err) {
        running = false;
        if (unmounted) return;
        // eslint-disable-next-line no-console
        console.error('[auth] config read failed:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
        // B149 — the config can come back (a cold instance loading its flags,
        // an instance the outbound breaker replaced), so a page gets one more
        // whole cycle. B149 fix-3 — immediately, with no timer: the cycle that
        // just failed spent four bounded reads getting here, which is all the
        // waiting a guessed 3 s was ever standing in for.
        if (retriesLeft > 0) {
          retriesLeft -= 1;
          void init(true);
          return;
        }
        // B167 fix-3 (F1) — asking is over. A route holding on `error` (the
        // first look) takes its terminal exit on this, instead of rendering
        // nothing for the life of the page.
        setConfigExhausted(true);
        configExhaustedRef.current = true;
        // B178 fix-2 — THE BOOT AS A WHOLE HAS ENDED, WITH NO CLIENT. A tap
        // waiting in `bootedClient` was waiting on exactly this; it stops
        // waiting now rather than at its own cap.
        getBootOutcome().resolve({ client: null });
        watchForLateClient();
        return;
      }

      // ── Past here a CLIENT EXISTS, and a throw is not a config failure ──
      if (unmounted) {
        running = false;
        return;
      }
      supabaseRef.current = client;
      setSupabase(client);
      // B178 fix-2 — THE BOOT AS A WHOLE HAS ENDED, WITH A CLIENT. Resolved
      // here rather than after `getSession`/the deep-link listener below: a
      // tap only needs a client to call `signInWithOAuth` on, and those steps
      // can throw without that being a reason to keep a tap waiting (see the
      // B167 fix-3 comment on the `catch` below).
      getBootOutcome().resolve({ client });
      // B149 — A CONFIG FAILURE IS NO LONGER LATCHED FOR THE LIFE OF THE
      // PAGE. This effect used to run once, with `[]` deps: a `/api/config`
      // that failed at boot left `error` set and `supabase` null until a
      // manual reload, even after the instance warmed up a second later.
      // A read that succeeds releases it.
      setError(null);
      setConfigExhausted(false);
      configExhaustedRef.current = false;

      try {
        // Part B — inside the Capacitor shell, sign-in returns on the
        // tayro:// deep link; the listener completes the session and
        // onAuthStateChange below picks it up like any other sign-in.
        if (isNativeApp()) {
          // apple-login-v1 — attribute the returning error to whichever
          // provider started the hop (see `pendingProviderRef`), NOT to
          // mapOAuthError's 'google' default.
          removeDeepLinkListener = await registerNativeAuthListener(client, (msg) =>
            raiseOAuthError(msg, pendingProviderRef.current),
          );
          if (unmounted) {
            removeDeepLinkListener();
            removeDeepLinkListener = null;
            return;
          }
        }

        // B147 — THE DEADLINE. See the constant's comment: `getSession()` is
        // never abandoned, only stopped waiting on here; the subscription
        // below shares its lock and still delivers a late session.
        const deadlinePassed = new Promise<typeof AUTH_GET_SESSION_DEADLINE>((resolve) => {
          deadlineTimer = setTimeout(
            () => resolve(AUTH_GET_SESSION_DEADLINE),
            AUTH_GET_SESSION_DEADLINE_MS,
          );
        });
        const sessionSettled = await Promise.race([client.auth.getSession(), deadlinePassed]);
        if (unmounted) return;
        if (sessionSettled === AUTH_GET_SESSION_DEADLINE) {
          // B147 — THE HOLD ENDS HERE, ON THIS LINE. `loading` clears via the
          // `finally` below immediately behind this branch (nothing else
          // waits on the timed-out call), instead of riding the platform's
          // own request timeout. No stored session to fall back to (see the
          // constant's comment) — the signed-out surface, until a real auth
          // event lands on the subscription just below.
          // eslint-disable-next-line no-console
          console.warn(
            '[auth] getSession did not settle within the boot deadline — signed out for now',
          );
          setSession(null);
        } else if (sessionSettled.error) {
          // getSession can fail on stale storage entries — treat as
          // "no session" rather than fatal error.
          // eslint-disable-next-line no-console
          console.warn(
            '[auth] getSession warning (treated as no session):',
            sessionSettled.error,
          );
          setSession(null);
        } else {
          setSession(sessionSettled.data.session ?? null);
          purgeForeignOnboardingState(sessionSettled.data.session?.user?.id);
        }

        const sub = client.auth.onAuthStateChange((event, nextSession) => {
          if (unmounted) return;
          setSession(nextSession ?? null);
          // ⚖️ B251 (2026-09-23) — A REAL SIGNED_IN/SIGNED_OUT OUTLIVES A
          // STALE CHIP. `clearOAuthErrorState` used to run only at the top
          // of a NEW explicit tap (`signInWithGoogle`/`signInWithApple`
          // below) — so a chip raised by a wedged Apple sheet survived both
          // the late-completion continuation's own eventual commit (which
          // reaches this provider ONLY through this subscription, never
          // through the tap's own promise, already long settled) and an
          // unrelated LATER sign-out. Either real event here means whatever
          // the chip was warning about is no longer the state of the world.
          if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
            clearOAuthErrorState();
          }
          if (nextSession) {
            purgeForeignOnboardingState(nextSession.user.id);
            // 2.0 room #10 (fix round 2, final sign-off) — a send of the account that signed out can fail late and write
            // its waiting words back after the SIGNED_OUT sweep; a session of anyone else removes every room queue key
            // that is not its own (the subscription replays INITIAL_SESSION on boot, so a cold start is covered too).
            clearQueuesExcept(nextSession.user.id);
          } else if (event === 'INITIAL_SESSION') {
            // F4 (2026-09-21) — auth-js fires INITIAL_SESSION to every new
            // subscriber, with `session: null` for any visitor who has no
            // stored session yet (vendored @supabase/auth-js 2.105.1,
            // GoTrueClient.js:3396-3435) — it is the SDK's replay on
            // subscribe, not a sign-out. Treating it like any other
            // null-session change deleted an anonymous first-time visitor's
            // own in-progress wizard blob (which can hold a minor's PAR-Q +
            // body metrics) on the very mount that discovers there is no
            // session yet. No-op here; the blob is left alone for this event.
          } else {
            // B109 (2026-09-21) — a session settling to null on a real CHANGE
            // event (SIGNED_OUT covers sign-out and an invalid refresh token)
            // clears the onboarding wizard blob unconditionally, the
            // signed-out user's own answers included, which can hold a
            // minor's PAR-Q + body metrics. purgeForeignOnboardingState is
            // keyed to a signed-in user id and is a no-op with none, which is
            // why sign-out used to leave that blob sitting on the device.
            clearPersistedState();
            // W1-TODAY-LAYOUT-MEMORY (Claude Design condition 1, 2026-10-18) — the layout
            // Today's loading frame remembers is per account and goes with the session, so a
            // shared phone or the dual athlete and coach account never draws another person's
            // page.
            clearTodayLayout();
            clearPlanShape();
            // 2.0 room #10 (fix round 1, critic and security 2026-10-30) — the room's queue of messages waiting for a
            // connection is the athlete's own words and goes with the session. The signed-out screen unmounts the room
            // before its hook could see the account go, so the sweep is here, by the key prefix, for every account.
            clearAllQueues();
          }
        });
        unsubscribe = () => sub.data.subscription.unsubscribe();
      } catch (err) {
        // ⚖️ B167 fix-3 (round-51 critic F1) — A THROW WITH A LIVE CLIENT IS
        // NOT A CONFIG FAILURE, so it does not set `error`. The dynamic
        // `import('@capacitor/app')` behind the native listener and
        // `getSession` on stale storage can both throw here on a device whose
        // config is perfectly fine; latching `error` on them put a working
        // client's route into the blank the first look holds in. The session
        // simply stays null, which every guard in the app already handles, and
        // the athlete lands on the signed-out door with a button on it.
        // eslint-disable-next-line no-console
        console.warn('[auth] session init failed (the client is live):', err);
      } finally {
        // B147 — a read that settled before the deadline (or an early
        // throw/return above it) leaves no timer behind.
        if (deadlineTimer !== undefined) clearTimeout(deadlineTimer);
        running = false;
        if (!unmounted) setLoading(false);
      }
    };

    /**
     * ⚖️ B417 — AN OUTAGE WITH NO CLIENT ENDS HERE, AND THE MODULE CAN STILL
     * BUILD ONE LATER.
     *
     * Past the exhausted mark this provider used to stop for the life of the
     * page: the landing's late ask (`reaskFlagsForLanding`, B398) or the
     * recovery cycle (`online` / `visibilitychange`, B149 fix-4) could build the
     * client once the server answered, the landing then showed both providers,
     * and a tap still raised "Sign-in is not ready yet" because this provider
     * never looked again. It looks again now, and only through the module's own
     * door: when `lib/supabase` says the config changed, ask it for the client
     * (`getSupabaseClient()`); only a client IT hands back is adopted, and
     * until then `bootedClient` answers exactly as before. Adoption runs the
     * whole of `init` past the config read (native deep-link listener,
     * `getSession`, `onAuthStateChange`), not just `setSupabase`, so the late
     * client is a booted one. ONCE: the watch is dropped the moment it adopts
     * (and on unmount), so a second outage afterwards does nothing new and the
     * listener setup can never run twice.
     *
     * The ask is deferred one microtask: the module notifies from inside its
     * own state changes, and a read it is about to start should find its
     * in-flight mark set, not race it. A rejection (still no client, or a
     * cycle that failed) is the same answer the provider already holds.
     */
    const watchForLateClient = (): void => {
      if (unmounted || stopWatchingForClient || supabaseRef.current) return;
      stopWatchingForClient = subscribeServerFlags(() => {
        if (unmounted || adoptionAttempting || running || supabaseRef.current) return;
        adoptionAttempting = true;
        void Promise.resolve()
          .then(() => getSupabaseClient())
          .then((late) => {
            if (unmounted || running || supabaseRef.current) return;
            if (stopWatchingForClient) stopWatchingForClient();
            stopWatchingForClient = null;
            // A tap during the outage left "Sign-in is not ready yet." on screen; with a client it is
            // stale, so adoption clears it (the clear the next tap would run). No other chip is touched.
            if (notReadyChipRef.current) clearOAuthErrorState();
            return init(false, late);
          })
          .catch(() => {
            /* still no client: the chip behaves as it did, and the watch stays */
          })
          .finally(() => {
            adoptionAttempting = false;
          });
      });
    };

    void init(false);

    return () => {
      unmounted = true;
      if (stopWatchingForClient) stopWatchingForClient();
      stopWatchingForClient = null;
      if (unsubscribe) unsubscribe();
      if (removeDeepLinkListener) removeDeepLinkListener();
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    return {
      supabase,
      session,
      user: session?.user ?? null,
      loading,
      error,
      configExhausted,
      signOut: async () => {
        const client = supabaseRef.current;
        if (!client) return;
        await client.auth.signOut();
      },
      // Phase 6b0 — initiate Google OAuth. Per design + kickoff brief:
      //   - provider: 'google' (Supabase OAuth, only provider in product)
      //   - redirectTo is ABSOLUTE: `${origin}/v2/today` — never a
      //     relative path (Supabase rejects relative redirects in some
      //     flows). Pre-merge gate verifies the URL is allowlisted on
      //     the Supabase Auth dashboard for localhost / Vercel preview /
      //     production.
      //   - prompt: 'select_account' so multi-account users see the
      //     Google account chooser (matches vanilla `auth.js:66`).
      // On error: store the chip copy via mapOAuthError; caller reads
      // `oauthError` and renders the chip below the hero CTA.
      signInWithGoogle: async () => {
        // B178 fix-3 — one sign-in in flight, however many taps or surfaces;
        // see `pendingSignInTokenRef`. Silent: no chip, no log. b178-fix4 —
        // armed with its own ownership token and belt timer; see
        // `armPendingSignIn`. This call OWNS `signInToken` for its own life.
        if (pendingSignInTokenRef.current !== null) return;
        const signInToken = armPendingSignIn();
        try {
          // B178 fix-2 — wait out the boot as a whole, capped at the tap's own
          // ceiling (see `bootedClient`); the chip is only for a boot that has
          // nothing to offer this tap, whichever of the two reasons that is.
          const booted = await bootedClient();
          const client = booted.client;
          if (!client) {
            // B149 / B178 fix-2 — THE BUTTON SAYS SOMETHING NOW. With no client
            // (a config read that failed on a device with no cached config, or
            // the tap's own wait outrunning both cycles) this used to `return`
            // silently — a tap, and nothing at all, forever. Nothing was
            // attempted, so this is not one of `mapOAuthError`'s buckets:
            // decision X's ratified pair names the true state instead.
            // eslint-disable-next-line no-console
            console.warn('[auth] sign-in tapped with no client after the boot wait:', error);
            raiseNotReadyError('google');
            return;
          }
          // Clear any prior error before kicking off a new attempt.
          clearOAuthErrorState();
          // apple-login-v1 — claim the deep-link return BEFORE the browser hop.
          pendingProviderRef.current = 'google';
          try {
            // Part B — the Capacitor shell signs in via the SYSTEM
            // browser (Google blocks WebView OAuth); web is unchanged.
            if (isNativeApp()) {
              await openNativeOAuth(client);
              return;
            }
            const { error: oauthErr } = await client.auth.signInWithOAuth({
              provider: 'google',
              options: {
                redirectTo: `${window.location.origin}/v2/today`,
                queryParams: { prompt: 'select_account' },
              },
            });
            if (oauthErr) {
              raiseOAuthError(oauthErr, 'google');
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[auth] signInWithGoogle threw:', err);
            raiseOAuthError(err, 'google');
          }
        } finally {
          releasePendingSignIn(signInToken);
        }
      },
      // apple-login-v1 — Sign in with Apple. Deliberately NOT folded into
      // `signInWithGoogle` with a provider argument: the two flows differ in
      // kind inside the shell (Apple runs a native sheet in-process; Google
      // must leave for the system browser and return on a deep link), and a
      // single function would hide that with a branch at every line.
      //
      // Shell (the fork is EXPLICIT — native means the plugin, full stop):
      //   native sheet → identity token → signInWithIdToken → session
      //   cancelled    → silent return (dismissing a sheet is not an error)
      //   unavailable  → SURFACED as a chip. It used to reroute silently
      //                  into the system-browser OAuth path; the Build 7
      //                  field report (2026-08-02, infinite spinner) killed
      //                  that: a silent fallback masks a broken native
      //                  binary on exactly the surface App Review judges,
      //                  and "the first device test MUST show the native
      //                  sheet" is unverifiable when a browser flow can
      //                  impersonate success. If the plugin isn't in this
      //                  build, the athlete is TOLD, and Google remains one
      //                  button away.
      //   wedged       → signInWithAppleNative times out (30s), throws
      //                  AppleSheetTimeoutError → retryable chip. The
      //                  spinner always has an escape.
      // Web:
      //   signInWithOAuth({ provider: 'apple' }) — byte-for-byte the Google
      //   call with a different provider string and no Google-only
      //   `prompt=select_account`.
      //
      // Either way the session arrives through `onAuthStateChange` above, so
      // everything downstream (route guards, <JoinCodeMount>'s invite stash,
      // the onboarding forward) is the path Google already proved.
      signInWithApple: async () => {
        // B178 fix-3 — the same shared guard, on the other door. See
        // `pendingSignInTokenRef`: one sign-in in flight covers both
        // providers. b178-fix4 — armed with the same ownership token and
        // belt timer as the other door; this call OWNS `signInToken`.
        if (pendingSignInTokenRef.current !== null) return;
        const signInToken = armPendingSignIn();
        try {
          // B178 fix-2 — the same capped wait, on the other door. See `bootedClient`.
          const booted = await bootedClient();
          const client = booted.client;
          if (!client) {
            // B149 / B178 fix-2 — the same true chip, on the other door.
            // eslint-disable-next-line no-console
            console.warn('[auth] sign-in tapped with no client after the boot wait:', error);
            raiseNotReadyError('apple');
            return;
          }
          clearOAuthErrorState();
          // Claim the deep-link return BEFORE the hand-off. The native sheet
          // returns in-process and never reaches the listener; the claim is
          // kept anyway so a stray callback arriving during an Apple attempt
          // is attributed to Apple, not to mapOAuthError's 'google' default.
          pendingProviderRef.current = 'apple';
          try {
            if (isNativeApp()) {
              const outcome = await signInWithAppleNative(client);
              // 'signed-in' → onAuthStateChange owns the rest, chip
              // included: B251 clears `oauthError` there on SIGNED_IN, so a
              // late-completion success (this call has already returned by
              // then) still takes a stale timeout chip down with it.
              // 'cancelled' → the athlete dismissed the sheet; say nothing.
              if (outcome === 'unavailable') {
                raiseOAuthError(new ApplePluginUnavailableError(), 'apple');
              }
              return;
            }
            const { error: oauthErr } = await client.auth.signInWithOAuth({
              provider: 'apple',
              options: { redirectTo: `${window.location.origin}/v2/today` },
            });
            if (oauthErr) {
              raiseOAuthError(oauthErr, 'apple');
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[auth] signInWithApple threw:', err);
            raiseOAuthError(err, 'apple');
          }
        } finally {
          releasePendingSignIn(signInToken);
        }
      },
      oauthError,
      oauthErrorProvider,
      clearOAuthError: clearOAuthErrorState,
    };
  }, [
    supabase,
    session,
    loading,
    error,
    configExhausted,
    oauthError,
    oauthErrorProvider,
    bootedClient,
    raiseOAuthError,
    raiseNotReadyError,
    clearOAuthErrorState,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Internal — exposed for hooks in this folder. Component code should
 * use the higher-level `useUser` / `useAuthFetch` hooks instead.
 */
export function useAuthContext(): AuthContextValue {
  return useContext(AuthContext);
}
